<script>
import utils from '@/utils/common';

import BaseButton from '@/components/BaseButton.vue';
import UserDetailDateLineTile from '@/components/user/tiles/UserDetailDateLineTile.vue';
import UserDetailStatisticTile from '@/components/user/tiles/UserDetailStatisticTile.vue';
import UserDetailBarChartTile from '@/components/user/tiles/UserDetailBarChartTile.vue';
import UserDetailContactsTile2 from '@/components/user/tiles/UserDetailContactsTile2.vue';

// данных формата  { date, event } для линейных графиков нет
// ролей в проектах ("заказчик" и "подрядчик") тоже нет; с ролями "исполнитель" и "наблюдатель" могут быть неточности

export default {
  name: 'UserDetailSummaryTab',
  components: {
    UserDetailDateLineTile,
    UserDetailStatisticTile,
    UserDetailBarChartTile,
    UserDetailContactsTile2,
    BaseButton,
  },
  props: {
    user: Object,
    statistics: Object,
  },
  computed: {
    accountProjectsData() {
      return this.statistics?.projects.map(p => ({ date: p.createdAt, value: p.value })).sort((a, b) => new Date(a.date) - new Date(b.date));
    },

    storageData() {
      return this.statistics?.storage.map(s => ({ date: s.createdAt, value: Math.round(utils.fileSize(s.value).value) })).sort((a, b) => new Date(a.date) - new Date(b.date));
    },

    totalProjects() {
      const projects = this.statistics?.projects;
      const membership = this.statistics?.membership;
      return [...projects, ...membership];
    },

    totalProjectByMonth() {
      const actualDate = new Date();
      const actualDay = actualDate.getDate();

      const totalProjects = this.totalProjects.map(p => ({ ...p, date: new Date(p.createdAt).getDate() }));

      return totalProjects.filter(p => p.date >= 1 && p.date <= actualDay);
    },

    totalProjectsData() {
      return this.totalProjects
        .map(p => ({ date: p.createdAt, value: p.value }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    },

    accountProjectsTile() {
      return {
        label: 'Проекты на аккаунте',
        info: [
          { value: this.user.projectParticipation.project.author, title: 'Активных проектов' },
          { value: this.user.projectParticipation.project.inactive, title: 'Архивных проектов' },
        ],
        data: this.accountProjectsData,
      };
    },

    storageTile() {
      const storage = utils.fileSize(this.user.tariff.maxStorage);
      const storageUsed = utils.fileSize(this.user.storageCount);

      return {
        label: `Хранилище, ${storage.unit.toUpperCase()}`,
        info: [
          { value: `${Math.round(storage.value)}`, title: `${storage.unit.toUpperCase()} доступно` },
          { value: `${Math.round(storageUsed.value)}`, title: `${storageUsed.unit.toUpperCase()} использовано` },
        ],
        data: this.storageData,
      };
    },

    totalProjectsTile() {
      const totalProjects = { ...this.user.projectParticipation.project };
      totalProjects.inActive = 0;
      const totalProjectsCount = Object.values(totalProjects).reduce((acc, val) => acc + val);
      const totalProjectByMonthCount = Math.max(...this.totalProjectByMonth.map(p => p.value));

      return {
        label: 'Все проекты',
        info: [
          { value: `${totalProjectsCount}`, title: 'Проекта активны' },
          { value: `${totalProjectByMonthCount}`, title: 'Проекта за месяц' },
        ],
        data: this.totalProjectsData,
      };
    },

    accountTiles() {
      return [
        { tile: this.accountProjectsTile, color: 'rgba(255, 216, 2, 1)' },
        { tile: this.storageTile, color: 'rgba(252, 142, 1, 1)' },
        { tile: this.totalProjectsTile, color: 'rgba(232, 49, 0, 1)' },
      ];
    },

    projectRoles() {
      return this.user.projectParticipation.project;
    },

    rolesStatistic() {
      return {
        author:   { label: 'Автор',           icon: 'author',       value: this.projectRoles.author,      title: 'Проектов' }, // eslint-disable-line
        admin:    { label: 'Администратор',   icon: 'manager',      value: this.projectRoles.admin,       title: 'Проектов' }, // eslint-disable-line
        work:     { label: 'Рабочий',         icon: 'contractor',   value: this.projectRoles.work,        title: 'Проектов' }, // eslint-disable-line
        standard: { label: 'Стандартный',     icon: 'member',       value: this.projectRoles.standard,    title: 'Проектов' }, // eslint-disable-line
      };
    },

    taskBarColors() {
      return [
        'rgba(165, 211, 146, 1)',
        'rgb(255, 208, 89)',
        'rgb(203, 203, 203)',
        'rgb(54, 128, 204)',
        'rgb(122, 94, 159)',
        'rgb(232, 104, 99)',
        'rgb(139, 139, 139)',
      ];
    },

    taskBarLabels() {
      return ['Новые', 'В работе', 'Пауза', 'Проверка', 'На доработку', 'Согласование', 'Завершена'];
    },

    taskBarData() {
      return Object.values(this.user.projectParticipation.task);
    },

    tasksBarConfig() {
      return {
        backgroundColors: this.taskBarColors,
        labels: this.taskBarLabels,
        barThickness: 150,
        borderWidth: 0,
        borderRadius: 5,
        stepSize: Math.round(Math.max(...this.taskBarData) / 3) + 3,
      };
    },
  },
};
</script>

<template lang="pug">
.b-user-detail-summary-tab
  UserDetailDateLineTile(v-for="tile in accountTiles" :key="tile.color" :config="tile")
  UserDetailStatisticTile._roles-tile(:statistic="rolesStatistic" label="Роли в проектах" isBorderShown)
  UserDetailBarChartTile._tasks-tile(:data="taskBarData" :config="tasksBarConfig" commonLabel="Задачи")
    //BaseButton(grey xs round) Список задач
</template>

<style scoped lang="scss">
.user-detail-summary-tab {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 38px;

  &__roles-tile {
    grid-column: 1 / 3 span;
  }

  &__tasks-tile {
    grid-column: 1 / 3 span;
  }

  &__contacts-tile {
    grid-column: 1 / 3 span;
  }
}
</style>
